/**
 * One push of the master config onto its containers: the master is saved
 * first when it has edits (the containers get the value on screen), then
 * pushed, then today's scheduled drops re-sync — the same tail a Save has.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';
import type { DropGenSection } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useMasterMutations } from '../../../../hooks/useMasterMutations';
import { useDropGeneratorMutations } from '../../../../useDropGeneratorMutations';
import { SECTION_LABEL } from '../../../campaign/master/rule-facts';

interface Options {
  masterId: string | null;
  masterName: string;
  countryKey: string;
  saveFirst: () => Promise<boolean>;
}

const containers = (n: number) => `${n} container${n === 1 ? '' : 's'}`;

export function useMasterPushRun({ masterId, masterName, countryKey, saveFirst }: Options) {
  const { pushMaster } = useMasterMutations();
  const { syncScheduledDay } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const [busy, setBusy] = useState(false);

  const syncToday = () =>
    syncScheduledDay.mutateAsync({ countryKey, containerId: masterId, kind: 'push' }).catch((err: unknown) => {
      error('Overridden, but today’s drafts could not be updated', `${getApiErrorMessage(err)} Use Generate to retry the sync.`);
    });

  /** `sections` absent = every section. True once the containers follow the master again. */
  const run = async (sections?: DropGenSection[]): Promise<boolean> => {
    if (!masterId) return false;
    setBusy(true);
    try {
      if (!(await saveFirst())) return false;
      const out = await pushMaster.mutateAsync({ id: masterId, sections });
      await syncToday();
      const what = sections ? sections.map((s) => SECTION_LABEL[s]).join(', ') : 'Every value';
      success(`${containers(out.containers.length)} overridden`, `${what} now comes from ${masterName}.`);
      return true;
    } catch (err) {
      error('Could not override the containers', getApiErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
}
